import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Trophy, Crown, Medal, Share2, Heart, TrendingUp, 
  Award, Sparkles, User
} from "lucide-react";
import { PageHero } from "@/components/PageHero";

interface LeaderboardUser {
  id: string | null;
  wallet_address: string;
  display_name: string | null;
  avatar_url: string | null;
  score: number;
  position: number;
}

type LeaderboardType = "top_seller" | "top_sharer" | "most_liked";

const LEADERBOARD_CONFIG: Record<LeaderboardType, {
  title: string;
  description: string;
  icon: React.ReactNode;
  scoreLabel: string;
}> = {
  top_seller: {
    title: "Top Sellers",
    description: "Creators with the most NFT sales",
    icon: <Trophy className="w-6 h-6" />,
    scoreLabel: "Sales"
  },
  top_sharer: {
    title: "Top Sharers", 
    description: "Most active NFT promoters",
    icon: <Share2 className="w-6 h-6" />,
    scoreLabel: "Shares"
  },
  most_liked: {
    title: "Most Liked",
    description: "Creators with the most liked NFTs",
    icon: <Heart className="w-6 h-6" />,
    scoreLabel: "Likes"
  }
};

export default function Leaderboard() {
  const [activeTab, setActiveTab] = useState<LeaderboardType>("top_seller");
  const [topSellers, setTopSellers] = useState<LeaderboardUser[]>([]);
  const [topSharers, setTopSharers] = useState<LeaderboardUser[]>([]);
  const [mostLiked, setMostLiked] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAllLeaderboards();

    // Set up realtime subscription for updates
    const channel = supabase
      .channel('leaderboard-updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'nfts' }, () => {
        fetchAllLeaderboards();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'nft_likes' }, () => {
        fetchAllLeaderboards();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'share_logs' }, () => {
        fetchAllLeaderboards();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchAllLeaderboards = async () => {
    setLoading(true);
    await Promise.all([
      fetchTopSellers(),
      fetchTopSharers(),
      fetchMostLiked()
    ]);
    setLoading(false);
  };

  const fetchTopSellers = async () => {
    try {
      // Get creators with most sales (sum of sales_count from their NFTs)
      const { data: nfts } = await supabase
        .from("nfts")
        .select("creator_wallet, sales_count")
        .not("creator_wallet", "is", null);

      if (!nfts) return;

      // Aggregate sales by creator
      const salesByCreator: Record<string, number> = {};
      nfts.forEach(nft => {
        if (nft.creator_wallet) {
          salesByCreator[nft.creator_wallet] = (salesByCreator[nft.creator_wallet] || 0) + nft.sales_count;
        }
      });

      // Get top 10 sellers
      const topWallets = Object.entries(salesByCreator)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      if (topWallets.length === 0) {
        setTopSellers([]);
        return;
      }

      // Get profiles for top wallets
      const walletAddresses = topWallets.map(([wallet]) => wallet);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, wallet_address, display_name, avatar_url")
        .in("wallet_address", walletAddresses);

      const leaderboard: LeaderboardUser[] = topWallets.map(([wallet, score], index) => {
        const profile = profiles?.find(p => p.wallet_address === wallet);
        return {
          id: profile?.id || null,
          wallet_address: wallet,
          display_name: profile?.display_name || null,
          avatar_url: profile?.avatar_url || null,
          score,
          position: index + 1
        };
      });

      setTopSellers(leaderboard);
    } catch (error) {
      console.error("Error fetching top sellers:", error);
    }
  };

  const fetchTopSharers = async () => {
    try {
      // Get share counts by wallet
      const { data: shares } = await supabase
        .from("share_logs")
        .select("wallet_address")
        .not("wallet_address", "is", null);

      if (!shares) return;

      // Aggregate shares by wallet
      const sharesByWallet: Record<string, number> = {};
      shares.forEach(share => {
        if (share.wallet_address) {
          sharesByWallet[share.wallet_address] = (sharesByWallet[share.wallet_address] || 0) + 1;
        }
      });

      // Get top 10 sharers
      const topWallets = Object.entries(sharesByWallet)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      if (topWallets.length === 0) {
        setTopSharers([]);
        return;
      }

      // Get profiles
      const walletAddresses = topWallets.map(([wallet]) => wallet);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, wallet_address, display_name, avatar_url")
        .in("wallet_address", walletAddresses);

      const leaderboard: LeaderboardUser[] = topWallets.map(([wallet, score], index) => {
        const profile = profiles?.find(p => p.wallet_address === wallet);
        return {
          id: profile?.id || null,
          wallet_address: wallet,
          display_name: profile?.display_name || null,
          avatar_url: profile?.avatar_url || null,
          score,
          position: index + 1
        };
      });

      setTopSharers(leaderboard);
    } catch (error) {
      console.error("Error fetching top sharers:", error);
    }
  };

  const fetchMostLiked = async () => {
    try {
      // Get NFTs with their likes and creator wallets
      const { data: nfts } = await supabase
        .from("nfts")
        .select("creator_wallet, likes_count")
        .not("creator_wallet", "is", null);

      if (!nfts) return;

      // Aggregate likes by creator
      const likesByCreator: Record<string, number> = {};
      nfts.forEach(nft => {
        if (nft.creator_wallet) {
          likesByCreator[nft.creator_wallet] = (likesByCreator[nft.creator_wallet] || 0) + nft.likes_count;
        }
      });

      // Get top 10 most liked
      const topWallets = Object.entries(likesByCreator)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      if (topWallets.length === 0) {
        setMostLiked([]);
        return;
      }

      // Get profiles
      const walletAddresses = topWallets.map(([wallet]) => wallet);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, wallet_address, display_name, avatar_url")
        .in("wallet_address", walletAddresses);

      const leaderboard: LeaderboardUser[] = topWallets.map(([wallet, score], index) => {
        const profile = profiles?.find(p => p.wallet_address === wallet);
        return {
          id: profile?.id || null,
          wallet_address: wallet,
          display_name: profile?.display_name || null,
          avatar_url: profile?.avatar_url || null,
          score,
          position: index + 1
        };
      });

      setMostLiked(leaderboard);
    } catch (error) {
      console.error("Error fetching most liked:", error);
    }
  };

  const getLeaderboardData = (type: LeaderboardType): LeaderboardUser[] => {
    switch (type) {
      case "top_seller": return topSellers;
      case "top_sharer": return topSharers;
      case "most_liked": return mostLiked;
    }
  };

  const config = LEADERBOARD_CONFIG[activeTab];
  const data = getLeaderboardData(activeTab);

  return (
    <div className="min-h-screen relative" style={PAGE_BG_STYLE}>
      <AppHeader />
      
      <main className="pt-32 sm:pt-36 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Hero Section using PageHero component */}
          <PageHero
            showLogo={false}
            title="Leaderboard"
            subtitle="Real-time rankings of top creators on FLOW Marketplace. Compete for badges and recognition!"
          />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as LeaderboardType)} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="top_seller" className="gap-2">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Top Sellers</span>
              <span className="sm:hidden">Sellers</span>
            </TabsTrigger>
            <TabsTrigger value="top_sharer" className="gap-2">
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Top Sharers</span>
              <span className="sm:hidden">Sharers</span>
            </TabsTrigger>
            <TabsTrigger value="most_liked" className="gap-2">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Most Liked</span>
              <span className="sm:hidden">Liked</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {/* Category Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-2 rounded-lg gradient-primary`}>
                <div className="text-primary-foreground">{config.icon}</div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{config.title}</h2>
                <p className="text-sm text-muted-foreground">{config.description}</p>
              </div>
            </div>

            {/* Leaderboard List */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-20 rounded-xl" />
                ))}
              </div>
            ) : data.length === 0 ? (
              <Card className="border-dashed border-2 border-muted-foreground/20">
                <CardContent className="py-12 text-center">
                  <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Rankings Yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Be the first to claim the top spot!
                  </p>
                  <Link to="/marketplace">
                    <Button variant="outline">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Explore Marketplace
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {data.map((user) => (
                  <LeaderboardCard 
                    key={user.wallet_address} 
                    user={user} 
                    config={config}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Badge Info */}
        <div className="mt-12">
          <Card className="bg-primary/10 border-2 border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">
                    Earn the {config.title.replace("Top ", "").replace("Most ", "")} Badge!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    The #1 ranked creator in this category earns an exclusive badge displayed on their profile.
                    Rankings update in real-time!
                  </p>
                  <Link 
                    to="/early-creators"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors mt-3"
                  >
                    View All Badges →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function LeaderboardCard({ 
  user, 
  config 
}: { 
  user: LeaderboardUser; 
  config: typeof LEADERBOARD_CONFIG[LeaderboardType];
}) {
  // Hide wallet, show generic name if no display_name
  const displayName = user.display_name || "Creator";

  const getPositionStyle = (position: number) => {
    if (position === 1) return { bg: "bg-gradient-to-br from-yellow-400 to-amber-500", text: "text-white" };
    if (position === 2) return { bg: "bg-gradient-to-br from-slate-300 to-slate-400", text: "text-slate-800" };
    if (position === 3) return { bg: "bg-gradient-to-br from-amber-600 to-amber-700", text: "text-white" };
    return { bg: "bg-muted", text: "text-muted-foreground" };
  };

  const posStyle = getPositionStyle(user.position);
  const profileLink = user.id ? `/profile/${user.id}` : "#";

  return (
    <Link to={profileLink}>
      <Card className="hover:border-primary/50 transition-all duration-200 hover:shadow-lg group">
        <CardContent className="p-4 flex items-center gap-4">
          {/* Position */}
          <div className={`w-10 h-10 rounded-full ${posStyle.bg} flex items-center justify-center flex-shrink-0`}>
            {user.position <= 3 ? (
              user.position === 1 ? <Crown className={`w-5 h-5 ${posStyle.text}`} /> :
              user.position === 2 ? <Medal className={`w-5 h-5 ${posStyle.text}`} /> :
              <Award className={`w-5 h-5 ${posStyle.text}`} />
            ) : (
              <span className={`font-bold ${posStyle.text}`}>{user.position}</span>
            )}
          </div>

          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex-shrink-0">
            {user.avatar_url ? (
              <img 
                src={user.avatar_url} 
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Info - Only display name, no duplicate wallet */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {displayName}
            </h3>
          </div>

          {/* Score */}
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-bold text-foreground">{user.score}</div>
            <div className="text-xs text-muted-foreground">{config.scoreLabel}</div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
